import React, { useCallback, useEffect, useReducer, useState, useRef } from 'react'
import PropTypes from 'prop-types'
import { Accordion, Card } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import cn from 'classnames'
import { isEqual } from 'lodash'

import CampaignHeader from 'components/Recommendations/Campaign/CampaignHeader'
import { SecondaryButton, TransparentBgButton } from 'components/Shared/Button'
import Menu from 'components/Shared/Menu'
import LoadingSpinner, { SpinnerSize } from 'components/Shared/LoadingSpinner'
import { AssetClass, campaignKeywords, Platform, tcDisplayName } from 'api/models'

import moreDots from 'images/icons/more_horiz.svg'
import share from 'images/icons/share.svg'
import edit from 'images/icons/edit.svg'

import { CampaignEditor } from 'components/Recommendations/Campaign/Edit'
import PortalModal from 'components/Shared/PortalModal'
import CampaignSetupProvider, {
  useCampaignSetup,
  useCampaignSetupDispatch,
} from 'components/Recommendations/Campaign/Context/CampaignSetupContext'
import { useToast } from 'components/Shared/AppToast/ToastProvider'
import PreviewAdsModal from '../Summary/PreviewAdsModal'
import {
  expandReelVariations,
  expandVariationId,
} from 'components/Recommendations/Campaign/Context/disabledVariations'
import { initStateObject, objectReducer } from 'reducers/default'
import { fetchPageInfos } from 'api/integrations'
import RunningCampaign from 'components/Recommendations/Campaign/RunningCampaign/RunningCampaign'
import { useCampaignConfigDispatch } from 'components/Recommendations/Campaign/RunningCampaign/CampaignConfig/CampaignConfigProvider'
import campaignStatusApi from 'domain/campaignStatus/api'
import {
  usePublishedCampaignSetups,
  findTasteCluster,
  toCampaignSetupCompat,
} from 'hooks/usePublishedCampaignSetups'
import {
  derivePlatformSelectionFromSetup,
  SetupPlatform,
} from 'components/Recommendations/Campaign/platform'
import {
  TARGETING_PLATFORMS,
  gatedTargetingPlatforms,
} from 'components/Recommendations/Campaign/Edit/CampaignForm/Targeting/targeting'

export default function RunningCampaigns({
  event,
  campaigns,
  initialGoal,
  refetchCampaigns,
  isFutureEvent,
  setSharedTc,
}) {
  const [editedTc, setEditedTc] = useState(null)
  const [activeKey, setActiveKey] = useState(null)
  const [activeTcMenu, setActiveTcMenu] = useState(null)
  const [fbPageInfo, setFbPageInfo] = useReducer(objectReducer, initStateObject)
  const [campaignStatus, setCampaignStatus] = useState({ loading: true, data: null, error: null })
  const {
    loading: campaignSetupsLoading,
    eventCampaign,
    tasteClusters,
    refetch: refetchPublishedCampaignSetups,
  } = usePublishedCampaignSetups(event.id)

  useEffect(() => {
    if (campaigns.length === 0 || campaignSetupsLoading) {
      return
    }

    const getInfo = async () => {
      const integration_details = eventCampaign?.integrationDetails || []
      const meta_profile = integration_details.find(
        (integration_detail) =>
          integration_detail.asset_class === AssetClass.profile &&
          integration_detail.platform === Platform.facebook,
      )
      if (meta_profile) {
        await fetchPageInfos(setFbPageInfo, meta_profile?.asset_id)
      }
    }

    getInfo()
  }, [campaigns, campaignSetupsLoading, eventCampaign])

  // Event-level fetch (one Google campaign + one Meta campaign back the
  // whole event, not one per TC) — shared across every RunningCampaignItem
  // below instead of each TC re-fetching the same status.
  useEffect(() => {
    let cancelled = false
    setCampaignStatus({ loading: true, data: null, error: null })

    campaignStatusApi.fetchAsync(event.id).then(({ data, error }) => {
      if (cancelled) return
      setCampaignStatus({ loading: false, data: data ?? null, error: error ?? null })
    })

    return () => {
      cancelled = true
    }
  }, [event.id])

  // Gate on the v4 fetch before mounting CampaignSetupProvider: its initial
  // state (isPublished/setup/shouldAutosync) is computed once from
  // `campaignSetup` on mount (plain useReducer semantics — a later prop
  // change doesn't re-run it), so a campaign whose taste cluster hasn't
  // loaded yet must not mount as if it were unpublished.
  if (campaignSetupsLoading) {
    return <LoadingSpinner size={SpinnerSize.SMALL} portalBackground={false} />
  }

  return (
    <>
      {campaigns.map((campaign, index) => {
        const tasteCluster = findTasteCluster(tasteClusters, campaign.tc, campaign.tc_run_id)
        const campaignSetup = toCampaignSetupCompat(tasteCluster, eventCampaign)

        return (
          <CampaignSetupProvider
            eventId={event.id}
            campaign={campaign}
            campaignSetup={campaignSetup}
            key={campaign.tc}
          >
            <RunningCampaignItem
              event={event}
              campaign={campaign}
              campaignSetup={campaignSetup}
              goal={initialGoal}
              setEditedTc={setEditedTc}
              activeKey={activeKey}
              setActiveKey={setActiveKey}
              index={index}
              isFutureEvent={isFutureEvent}
              setSharedTc={setSharedTc}
              activeTcMenu={activeTcMenu}
              setActiveTcMenu={setActiveTcMenu}
              isBeingEdited={editedTc === campaign.tc}
              fbPageInfo={fbPageInfo}
              campaignStatus={campaignStatus.data}
              onEditCancelled={() => setEditedTc(null)}
              onEditSuccess={() => {
                setEditedTc(null)
                refetchCampaigns()
                refetchPublishedCampaignSetups()
              }}
              onEditCampaignClicked={(e) => {
                e.stopPropagation()
                if (!activeKey || index !== Number(activeKey)) setActiveKey(index.toString())
                setEditedTc(campaign.tc)
              }}
            />
          </CampaignSetupProvider>
        )
      })}
    </>
  )
}

RunningCampaigns.propTypes = {
  event: PropTypes.object.isRequired,
  campaigns: PropTypes.arrayOf(Object),
  setSharedTc: PropTypes.func.isRequired,
  initialGoal: PropTypes.string,
  refetchCampaigns: PropTypes.func.isRequired,
  isFutureEvent: PropTypes.bool,
}

function RunningCampaignItem({
  event,
  campaign,
  campaignSetup,
  goal,
  activeKey,
  setActiveKey,
  isFutureEvent,
  index,
  setSharedTc,
  activeTcMenu,
  setActiveTcMenu,
  isBeingEdited,
  onEditCampaignClicked,
  onEditCancelled,
  onEditSuccess,
  fbPageInfo,
  campaignStatus,
}) {
  const { t } = useTranslation()
  const { loading, hasPendingMedia, setup } = useCampaignSetup()
  const [adsPreviewOpen, setAdsPreviewOpen] = useState(false)
  const accordionRef = useRef()
  const { hasPendingTask: hasPendingReconfigureTask } = useCampaignConfigDispatch()

  // Read-only view sources a gated platform's targeting from
  // campaign_targeting, not this TC's own (possibly stripped) field —
  // unconditional across every TC, since campaign_targeting is meant to be
  // identical for all of them once gated (unlike the setup-time form, the
  // read view doesn't need "which TC is #1"). Which platforms are gated is one
  // rule, defined once in targeting.js — derived, not re-implemented.
  const gatedPlatforms = gatedTargetingPlatforms(setup)
  const isGoogleDisplay = gatedPlatforms.includes(TARGETING_PLATFORMS.google)
  const isMetaGoogleCombined = gatedPlatforms.includes(TARGETING_PLATFORMS.fb)
  const bothPlatformsSelected = derivePlatformSelectionFromSetup(setup) === SetupPlatform.both

  const canEdit = !isBeingEdited && !hasPendingReconfigureTask()
  // v4 replacement for isCampaignRunning(campaign, {validateNotEnded: true}):
  // editable as long as at least one published platform's campaign hasn't
  // ended yet. Conservative default (hidden) while status hasn't loaded, or
  // when neither platform has published — same fail-closed intent the old
  // status-flag check had.
  const editablePlatforms = [campaignStatus?.google, campaignStatus?.meta].filter(
    (platform) => platform?.published,
  )
  const isEditableOnAnyPlatform =
    editablePlatforms.length > 0 && editablePlatforms.some((platform) => !platform.hasEnded)

  return (
    <Accordion activeKey={activeKey?.toString()} className="p-0">
      <Card
        ref={accordionRef}
        className={cn('campaign', {
          active: activeKey && Number(activeKey) === index,
        })}
      >
        <Accordion.Toggle eventKey={index.toString()}>
          <Card.Header as={Card.Header} className="pt-3">
            <CampaignHeader
              campaign={campaign}
              headerStatus={null}
              onClick={() => {
                setActiveKey(activeKey && index === Number(activeKey) ? null : index.toString())
              }}
              showActiveBadge={isFutureEvent}
            >
              <>
                {/* <PlatformStatusBadges google={campaignStatus?.google} meta={campaignStatus?.meta} /> */}
                {isFutureEvent && (
                  <div className="recommendation__preview_ads summary_item_button">
                    <SecondaryButton
                      text={t('Recommendations.summary.previewAds')}
                      color="navy"
                      size="small"
                      disabled={loading || hasPendingMedia}
                      onClick={() => setAdsPreviewOpen(true)}
                    />
                  </div>
                )}
                {isFutureEvent && (
                  <div
                    className="recommendation__more-dots_wrapper"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <img
                      style={{ width: 24 }}
                      src={moreDots}
                      alt="Campaign Options"
                      onClick={() => setActiveTcMenu(campaign.tc)}
                    />
                  </div>
                )}
                <div
                  className="recommendation__chevron"
                  onClick={() =>
                    setActiveKey(activeKey && index === Number(activeKey) ? null : index.toString())
                  }
                ></div>
              </>
              {isFutureEvent && (
                <Menu
                  options={[
                    {
                      id: 'share',
                      title: t('common.share'),
                      optionType: 'button',
                      onClick: () => setSharedTc(campaign.tc),
                      icon: share,
                    },
                    {
                      id: 'edit',
                      title: t('common.edit'),
                      disabled: !canEdit,
                      hidden: !isEditableOnAnyPlatform,
                      optionType: 'button',
                      onClick: (e) => {
                        if (!canEdit) return
                        onEditCampaignClicked(e, index, campaign)
                      },
                      icon: edit,
                    },
                  ]}
                  toggleMenu={() => setActiveTcMenu(null)}
                  open={activeTcMenu === campaign.tc}
                />
              )}
            </CampaignHeader>
          </Card.Header>
        </Accordion.Toggle>
        <Accordion.Collapse
          eventKey={index.toString()}
          onEntered={() => {
            if (accordionRef.current) {
              accordionRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
              })
            }
          }}
        >
          <Card.Body className="pt-0">
            <hr className="divider" />
            {isBeingEdited && setup ? (
              <EditedCampaign
                event={event}
                campaign={campaign}
                goal={goal}
                adsPreviewOpen={adsPreviewOpen}
                setAdsPreviewOpen={setAdsPreviewOpen}
                fbPageInfo={fbPageInfo}
                onCancel={onEditCancelled}
                onSuccess={onEditSuccess}
                isFirstTc={index === 0}
              />
            ) : (
              <RunningCampaignWrapper
                key={JSON.stringify(campaignSetup?.creative ?? {})}
                eventId={event.id}
                tc={campaign.tc}
                tc_run_id={campaign.tc_run_id}
                keywords={campaignKeywords(campaign).headline}
                integrationDetails={campaignSetup?.integration_details}
                tasteCluster={t('Recommendations.campaign.recommendationTitle', {
                  tc: tcDisplayName(campaign),
                })}
                creative={campaignSetup?.creative ?? {}}
                targeting={campaignSetup?.targeting}
                isGoogleDisplay={isGoogleDisplay}
                campaignTargetingGoogle={campaignSetup?.campaign_targeting?.google}
                isMetaGoogleCombined={isMetaGoogleCombined}
                campaignTargetingFb={campaignSetup?.campaign_targeting?.fb}
                bothPlatformsSelected={bothPlatformsSelected}
                adsPreviewOpen={adsPreviewOpen}
                fbPageInfo={fbPageInfo}
                onClosePreview={() => setAdsPreviewOpen(false)}
              />
            )}
          </Card.Body>
        </Accordion.Collapse>
      </Card>
    </Accordion>
  )
}

RunningCampaignItem.propTypes = {
  event: PropTypes.object.isRequired,
  campaign: PropTypes.object.isRequired,
  campaignSetup: PropTypes.shape({
    integration_details: PropTypes.array,
    creative: PropTypes.object,
    targeting: PropTypes.array,
  }),
  goal: PropTypes.string,
  setEditedTc: PropTypes.func.isRequired,
  activeKey: PropTypes.string,
  setActiveKey: PropTypes.func.isRequired,
  isFutureEvent: PropTypes.bool,
  index: PropTypes.number.isRequired,
  setSharedTc: PropTypes.func.isRequired,
  activeTcMenu: PropTypes.string,
  setActiveTcMenu: PropTypes.func.isRequired,
  isBeingEdited: PropTypes.bool,
  onEditCampaignClicked: PropTypes.func,
  onEditCancelled: PropTypes.func,
  onEditSuccess: PropTypes.func,
  fbPageInfo: PropTypes.object,
  campaignStatus: PropTypes.shape({
    google: PropTypes.object,
    meta: PropTypes.object,
  }),
}

function EditedCampaign({
  event,
  campaign,
  goal,
  onCancel,
  onSuccess,
  adsPreviewOpen,
  setAdsPreviewOpen,
  fbPageInfo,
  isFirstTc = false,
}) {
  const { t } = useTranslation()

  const { checkDisabledVariationsIntegrity, editPublishedSetup, updateDisabledAdVariations } =
    useCampaignSetupDispatch()
  const { setup, disabledVariations } = useCampaignSetup()

  const [currentDisabledVariations, setCurrentDisabledVariations] = useState(disabledVariations)
  const [disabledVariationsWarningModalOpen, setDisabledVariationsWarningModalOpen] =
    useState(false)
  const { addNotification } = useToast()

  const toggleVariation = (ad, tc) => {
    const variationIds = expandVariationId(ad.variationId, setup.creatives, tc)
    const tcDisabledVariations = [...(currentDisabledVariations[tc] || [])]

    for (const variationId of variationIds) {
      const existingIndex = tcDisabledVariations.indexOf(variationId)
      if (existingIndex > -1) {
        tcDisabledVariations.splice(existingIndex, 1)
      } else {
        tcDisabledVariations.push(variationId)
      }
    }

    setCurrentDisabledVariations({
      ...currentDisabledVariations,
      [tc]: tcDisabledVariations,
    })
  }

  const submitCampaignSetup = useCallback(
    (forceSubmit = false) => {
      const disabledVariationsChanged = !checkDisabledVariationsIntegrity(currentDisabledVariations)
      if (disabledVariationsChanged && forceSubmit !== true) {
        setDisabledVariationsWarningModalOpen(true)
        return
      }

      editPublishedSetup(
        ({ success, tc, tc_run_id: tcRunId }) => {
          if (success) {
            addNotification({
              message: t('Toasts.Campaigns.campaignEdited.message'),
              description: t('Toasts.Campaigns.campaignEdited.description'),
            })
            onSuccess({ tc, tcRunId })
          }
        },
        currentDisabledVariations,
        isFirstTc,
      )
    },
    [
      addNotification,
      editPublishedSetup,
      onSuccess,
      checkDisabledVariationsIntegrity,
      currentDisabledVariations,
      isFirstTc,
      t,
    ],
  )

  const onModalClose = (_, confirmed = false) => {
    const close = () => setAdsPreviewOpen(false)

    if (confirmed) {
      updateDisabledAdVariations(
        expandReelVariations(currentDisabledVariations, setup.creatives),
        () => {},
        false,
      )
      return close()
    }

    if (!isEqual(disabledVariations, currentDisabledVariations)) {
      setDisabledVariationsWarningModalOpen(true)
    } else {
      close()
    }
  }

  return (
    <>
      <CampaignEditor
        eventId={event.id}
        eventDate={event.date}
        campaign={campaign}
        goal={goal}
        onCancel={onCancel}
        onUpdate={submitCampaignSetup}
        eventInfo={event}
        isFirstTc={isFirstTc}
      />
      <PortalModal
        isOpen={disabledVariationsWarningModalOpen}
        onClickOutside={() => setDisabledVariationsWarningModalOpen(false)}
      >
        <div className="content compact text-center" style={{ maxWidth: '420px' }}>
          <h1 className="autofill_warning-header">
            {t('Recommendations.summary.adVariationsRemainDisabled')}
          </h1>
          <p className="autofill_warning-msg">
            {t('Recommendations.summary.adVariationsRemainDisabledDescription')}
          </p>
          <div className="footer">
            <TransparentBgButton
              text={t('common.cancel')}
              onClick={() => {
                setDisabledVariationsWarningModalOpen(false)
              }}
              color="navy"
            />
            <SecondaryButton
              color="orange"
              text={t('common.proceed')}
              onClick={(e) => {
                e.stopPropagation()
                setDisabledVariationsWarningModalOpen(false)
                setAdsPreviewOpen(false)
                submitCampaignSetup(true)
              }}
              type="button"
              disabledTitle=""
            />
          </div>
        </div>
      </PortalModal>
      <PreviewAdsModal
        isOpen={adsPreviewOpen && !disabledVariationsWarningModalOpen}
        onClose={onModalClose}
        creatives={setup?.creatives}
        setup={setup}
        pageInfo={fbPageInfo?.content}
        disabledVariations={currentDisabledVariations}
        onToggleVariation={toggleVariation}
      />
    </>
  )
}

EditedCampaign.propTypes = {
  event: PropTypes.object.isRequired,
  campaign: PropTypes.object.isRequired,
  onCancel: PropTypes.func.isRequired,
  goal: PropTypes.string.isRequired,
  onSuccess: PropTypes.func.isRequired,
  adsPreviewOpen: PropTypes.bool,
  setAdsPreviewOpen: PropTypes.func.isRequired,
  fbPageInfo: PropTypes.object,
  isFirstTc: PropTypes.bool,
}

function RunningCampaignWrapper({ adsPreviewOpen, onClosePreview, fbPageInfo, ...rest }) {
  const { setup, disabledVariations } = useCampaignSetup()

  return (
    <>
      <RunningCampaign {...rest} />
      <PreviewAdsModal
        isOpen={adsPreviewOpen}
        onClose={onClosePreview}
        creatives={setup?.creatives}
        setup={setup}
        pageInfo={fbPageInfo?.content}
        disabledVariations={disabledVariations}
      />
    </>
  )
}

RunningCampaignWrapper.propTypes = {
  onClosePreview: PropTypes.func.isRequired,
  adsPreviewOpen: PropTypes.bool,
  fbPageInfo: PropTypes.object,
}
